import { useState } from 'react';
import { useRegion, REGIONS } from '../context/RegionContext';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Globe, MapPin, Check, ChevronDown } from 'lucide-react';

export function RegionBanner() {
  const { 
    showBanner, 
    detectedCountry, 
    region, 
    confirmRegion, 
    changeRegion 
  } = useRegion();
  
  const [showRegionPicker, setShowRegionPicker] = useState(false);

  if (!showBanner || !region) return null;

  return (
    <>
      {/* Sticky Banner */}
      <div 
        className="fixed bottom-0 left-0 right-0 z-50 bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-4 py-3 shadow-lg"
        data-testid="region-banner"
      >
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <MapPin className="w-5 h-5" />
            </div>
            <div>
              <p className="font-medium text-sm sm:text-base">
                Detected Country: <span className="font-bold">{detectedCountry?.name || 'Unknown'}</span>
              </p>
              <p className="text-xs sm:text-sm text-white/80">
                Pricing in {region.currencySymbol} ({region.currency}) • {region.paymentGateway === 'payfast' ? 'PayFast' : 'PayPal'} payments
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm"
              className="text-white hover:bg-white/20"
              onClick={() => setShowRegionPicker(true)}
            >
              <Globe className="w-4 h-4 mr-1" />
              Change Region
            </Button>
            <Button 
              size="sm"
              className="bg-white text-indigo-600 hover:bg-white/90"
              onClick={confirmRegion}
            >
              <Check className="w-4 h-4 mr-1" />
              Confirm
            </Button>
          </div>
        </div>
      </div>

      {/* Region Picker Dialog */}
      <Dialog open={showRegionPicker} onOpenChange={setShowRegionPicker}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-indigo-500" />
              Select Your Region
            </DialogTitle>
            <DialogDescription>
              Choose your region to see pricing in your local currency
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 mt-4">
            {Object.values(REGIONS).map((r) => (
              <button
                key={r.code}
                className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                  region.code === r.code
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-slate-200 hover:border-indigo-200 hover:bg-slate-50'
                }`}
                onClick={() => {
                  changeRegion(r.code, true);
                  setShowRegionPicker(false);
                }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{r.flag}</span>
                    <div>
                      <p className="font-semibold text-slate-900">{r.name}</p>
                      <p className="text-sm text-slate-500">
                        {r.currencySymbol}{r.pricePerUnit} per unit/month • {r.paymentGateway === 'payfast' ? 'PayFast' : 'PayPal'}
                      </p>
                    </div>
                  </div>
                  {region.code === r.code && (
                    <Check className="w-5 h-5 text-indigo-500" />
                  )}
                </div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Compact region selector for header/settings
export function RegionSelector({ variant = 'default' }) {
  const { region, changeRegion, detectedCountry } = useRegion();
  const [open, setOpen] = useState(false);

  if (!region) return null;

  if (variant === 'compact') {
    return (
      <div className="relative">
        <button
          onClick={() => setOpen(!open)}
          className="flex items-center gap-1.5 px-2 py-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          data-testid="region-selector"
        >
          <span className="text-lg">{region.flag}</span>
          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
            {region.currency}
          </span>
          <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
        </button>
        
        {open && (
          <>
            <div 
              className="fixed inset-0 z-40" 
              onClick={() => setOpen(false)} 
            />
            <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 z-50 overflow-hidden">
              {Object.values(REGIONS).map((r) => (
                <button
                  key={r.code}
                  className={`w-full px-4 py-3 flex items-center gap-3 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors ${
                    region.code === r.code ? 'bg-indigo-50 dark:bg-indigo-900/30' : ''
                  }`}
                  onClick={() => {
                    changeRegion(r.code, true);
                    setOpen(false);
                  }}
                >
                  <span className="text-xl">{r.flag}</span>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-slate-900 dark:text-white text-sm">{r.name}</p>
                    <p className="text-xs text-slate-500">{r.currencySymbol}{r.pricePerUnit}/unit</p>
                  </div>
                  {region.code === r.code && (
                    <Check className="w-4 h-4 text-indigo-500" />
                  )}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <button
      onClick={() => setOpen(!open)}
      className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
    >
      <span className="text-xl">{region.flag}</span>
      <div className="text-left">
        <p className="text-sm font-medium text-slate-900 dark:text-white">{region.name}</p>
        <p className="text-xs text-slate-500">{region.currency}</p>
      </div>
      <ChevronDown className="w-4 h-4 text-slate-400 ml-2" />
    </button>
  );
}

export default RegionBanner;
